import { useEffect, useRef } from 'react'
import { useDependencyContext } from '../GanttDependency/DependencyContext'
import { useCriticalPathContext } from '../GanttCriticalPath/CriticalPathContext'

type UseRowContextRegistrationParams = {
  rowIndex: number
  wbsItem:
    | {
        uuid?: string
        scheduledDate?: { startDate?: string; endDate?: string }
        wbsItemType?: { isProcess?: () => boolean }
      }
    | undefined
  projectPlanUuid?: string
}

/**
 * Registers row date/info with DependencyContext and CriticalPathContext,
 * and notifies CriticalPathContext when the row is unmounted (collapsed/hidden).
 */
export const useRowContextRegistration = ({
  rowIndex,
  wbsItem,
  projectPlanUuid,
}: UseRowContextRegistrationParams): void => {
  const dependencyContext = useDependencyContext()
  const criticalPathContext = useCriticalPathContext()

  // Register row dates with DependencyContext for dependency line drawing
  useEffect(() => {
    if (
      dependencyContext &&
      rowIndex !== undefined &&
      rowIndex !== null &&
      rowIndex >= 0
    ) {
      const scheduledStart = wbsItem?.scheduledDate?.startDate
        ? new Date(wbsItem.scheduledDate.startDate)
        : null
      const scheduledEnd = wbsItem?.scheduledDate?.endDate
        ? new Date(wbsItem.scheduledDate.endDate)
        : null
      dependencyContext.updateRowDates(rowIndex, {
        scheduledStart,
        scheduledEnd,
        wbsItemUuid: wbsItem?.uuid,
        projectPlanUuid,
      })
    }
  }, [
    dependencyContext,
    rowIndex,
    wbsItem?.scheduledDate?.startDate,
    wbsItem?.scheduledDate?.endDate,
    wbsItem?.uuid,
  ])

  // Register row info with CriticalPathContext
  useEffect(() => {
    if (
      criticalPathContext &&
      rowIndex !== undefined &&
      rowIndex !== null &&
      rowIndex >= 0
    ) {
      const isProcess = wbsItem?.wbsItemType?.isProcess?.() ?? false
      const wbsItemUuid = wbsItem?.uuid ?? null
      criticalPathContext.updateRowInfo(rowIndex, {
        isProcess,
        wbsItemUuid,
        projectPlanUuid,
      })
    }
  }, [criticalPathContext, rowIndex, wbsItem?.wbsItemType, wbsItem?.uuid])

  // Notify context when cell is unmounted (row collapsed/hidden).
  // Uses refs to capture latest values without adding effect dependencies.
  const cpContextRef = useRef(criticalPathContext)
  cpContextRef.current = criticalPathContext
  const wbsItemUuidRef = useRef(wbsItem?.uuid)
  wbsItemUuidRef.current = wbsItem?.uuid
  useEffect(() => {
    return () => {
      const uuid = wbsItemUuidRef.current
      if (uuid) {
        cpContextRef.current?.notifyRowRemoved(uuid)
      }
    }
  }, [])
}
